import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { addressZ, AddressType } from '@libs/schemas';
import { useEffect, useState, FC } from 'react';
import { Loader } from 'src/components/Loader';
import { trpc } from 'src/trpc';
import { z } from 'zod';
import { useToast } from 'components/useToast';
import { GooglePlacesAutocomplete } from './GooglePlacesAutocomplete';

const addressSchema = addressZ.omit({ userId: true });
export type AddressZ = z.infer<typeof addressSchema> & { _id?: string; googlePlacesId?: string };

const defaultAddress: AddressZ = {
  line1: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  line2: '',
  type: '' as AddressType,
};

interface Props {
  existingAddress?: AddressZ;
  googlePlacesId?: string;
  addressType: 'delivery' | 'billing';
  userId: string;
  onSuccess?: (addressId: string) => void;
  showSaveButton?: boolean;
  onFormChange?: (address: AddressZ, placeId: string) => void;
}

export const AddressForm: FC<Props> = ({
  existingAddress,
  googlePlacesId = '',
  addressType = 'delivery',
  userId,
  onSuccess,
  showSaveButton = true,
  onFormChange,
}) => {
  const [address, setAddress] = useState<AddressZ>(existingAddress || defaultAddress);
  const [placeId, setPlaceId] = useState(googlePlacesId);
  const [error, setError] = useState<string>('');
  const [addressSearchValue, setAddressSearchValue] = useState<string>('');
  const utils = trpc.useUtils();
  const { successToast, errorToast } = useToast();

  const { mutate: upsertAddress, isPending } =
    trpc.admin.address.adminUpsertUserAddress.useMutation({
      onSuccess: (data) => {
        utils.admin.address.adminListUserAddresses.invalidate(userId);
        setError('');

        // Call the parent's onSuccess callback with the address ID
        if (onSuccess && data?.addressId) {
          successToast({
            title: 'Address Created',
            description: 'Address has been successfully created.',
          });
          onSuccess(data.addressId);
        } else if (onSuccess) {
          // Address was created but no ID returned - this shouldn't happen but let's handle it
          errorToast({
            title: 'Address Creation Issue',
            description:
              'Address was created but could not be selected automatically. Please refresh and select it manually.',
          });
        } else {
          // This is an edit/update operation
          successToast({
            title: 'Address Updated',
            description: 'Address has been successfully updated.',
          });
        }
      },
      onError: (err) => {
        setError(err.message);
        errorToast({
          title: 'Address Error',
          description: err.message || 'Failed to save address. Please try again.',
        });
      },
    });
  const setAddressValue = (key: keyof typeof address, value: string) => {
    setError('');
    const newAddress = { ...address, [key]: value };
    setAddress(newAddress);

    // Clear placeId when manually editing fields other than line2
    const shouldClearPlaceId = key !== 'line2' && placeId;
    if (shouldClearPlaceId) {
      setPlaceId('');
    }

    const finalPlaceId = shouldClearPlaceId ? '' : placeId;

    if (onFormChange) {
      onFormChange(newAddress, finalPlaceId);
    }
  };

  useEffect(() => {
    if (existingAddress) {
      setAddress(existingAddress);
      setAddressSearchValue(existingAddress.line1);
    }
    if (googlePlacesId) {
      setPlaceId(googlePlacesId);
    }
  }, []);

  if (isPending) {
    return <Loader title={`Updating your ${addressType} address`} />;
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      <GooglePlacesAutocomplete
        value={addressSearchValue}
        onChange={(value) => {
          setAddressSearchValue(value);

          // Always update line1 when user types (this is manual entry)
          const newAddress = { ...address, line1: value };
          setAddress(newAddress);

          // Clear placeId if it exists - this is now a manual entry
          if (placeId) {
            setPlaceId('');
          }

          // Notify parent of the change
          if (onFormChange) {
            onFormChange(newAddress, ''); // Always pass empty placeId when manually typing
          }
        }}
        placeholder="Start typing your address"
        onPlaceSelect={(googlePlace, addressData) => {
          const address = {
            ...addressData,
            type: addressType,
          };
          if (!address.line1 || !address.city || !address.state || !address.postalCode) {
            setError('Invalid Address');
            setAddress(defaultAddress);
            setPlaceId('');
            setAddressSearchValue('');
            if (onFormChange) {
              onFormChange(defaultAddress, '');
            }
            return;
          } else {
            setError('');
            setAddress(address);
            setPlaceId(googlePlace.place_id);
            setAddressSearchValue(address.line1);
            if (onFormChange) {
              onFormChange(address, googlePlace.place_id);
            }
          }
        }}
      />
      <div className="flex flex-col gap-2 w-full">
        <label className="label">Address Line 2</label>
        <Input
          className="bg-white"
          value={address.line2 || ''}
          placeholder="Apt, suite, etc."
          onChange={(e) => setAddressValue('line2', e.currentTarget.value)}
        />
      </div>
      <div className="flex flex-row w-full gap-2">
        <div className="flex flex-col gap-2">
          <label className="label">City</label>
          <Input
            className="bg-white"
            value={address.city}
            placeholder="City"
            onChange={(e) => setAddressValue('city', e.currentTarget.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="label">State</label>
          <Input
            className="bg-white uppercase"
            value={address.state}
            placeholder="e.g., FL"
            maxLength={2}
            minLength={2}
            onChange={(e) => setAddressValue('state', e.currentTarget.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="label">ZIP Code</label>
          <Input
            className="bg-white"
            value={address.postalCode}
            placeholder="ZIP Code"
            onChange={(e) => setAddressValue('postalCode', e.currentTarget.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="label">Country</label>
          <Input
            className="bg-white uppercase"
            value={address.country}
            placeholder="e.g., US"
            maxLength={2}
            minLength={2}
            onChange={(e) => setAddressValue('country', e.currentTarget.value)}
          />
        </div>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}
      {showSaveButton && (
        <Button
          className="w-[130px]"
          variant={'default'}
          disabled={
            isPending ||
            !!error ||
            !address.line1 ||
            !address.city ||
            !address.state ||
            !address.postalCode
          }
          onClick={() => {
            const payload = {
              placeId,
              line2: address.line2 || '',
              addressType,
              userId,
              addressId: existingAddress?._id,
              // Add manual address data for backend processing
              manualAddress: !placeId
                ? {
                    line1: address.line1,
                    city: address.city,
                    state: address.state,
                    postalCode: address.postalCode,
                    country: address.country || 'US',
                  }
                : undefined,
            };
            upsertAddress(payload);
          }}
        >
          Save Changes
        </Button>
      )}
    </div>
  );
};
